import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import {
  ContextMaterialIndexingStatus,
  isContextMaterialIndexingReady,
  KnowledgeDocumentFacet,
} from '@digital-twins/shared-types'
import { knowledgeApi, KnowledgeDocumentWithStages } from '@/lib/api'
import i18n from '@/i18n'
import { useKnowledgeDocumentIndexingUpdates } from '../KnowledgeHubPage/useKnowledgeDocumentIndexingUpdates'
import { documentTitle } from '../KnowledgeHubPage/libraryHelpers'
import { PagePreviewState } from './PreviewPane'

const PAGE_BATCH_SIZE = 2

export interface UseKnowledgeDocumentDetailOptions {
  documentToken: string | undefined
  /** Deep-linked page to select on first load, e.g. from a `?page=` URL param. */
  initialPage?: number | null
  /** A deep link asked for a page we could not resolve. */
  initialPageUnknown?: boolean
  /** `?t=` media timestamp, forwarded to the audio/video renderer. */
  startAt?: string | null
  /** Fires whenever the resolved page changes, so a caller can mirror it into its own URL. */
  onPageResolved?: (pageNumber: number) => void
  /** Fires after a successful delete, so a caller can navigate away or close an overlay. */
  onDeleted?: () => void
}

/**
 * All data-fetching, pagination, and mutation logic for a single knowledge
 * document. Shared by the full-page detail view and the library overlay pane
 * so both stay in lockstep without duplicating the prefetch/cache machinery.
 */
export function useKnowledgeDocumentDetail({
  documentToken,
  initialPage = null,
  initialPageUnknown = false,
  startAt = null,
  onPageResolved,
  onDeleted,
}: UseKnowledgeDocumentDetailOptions) {
  const [doc, setDoc] = useState<KnowledgeDocumentWithStages | null>(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [facetBusy, setFacetBusy] = useState(false)
  const [savingDetails, setSavingDetails] = useState(false)
  const [instructionsDraft, setInstructionsDraft] = useState('')
  const [instructionsBusy, setInstructionsBusy] = useState(false)
  const [replacing, setReplacing] = useState(false)
  const replaceInputRef = useRef<HTMLInputElement | null>(null)
  const [pagesLoading, setPagesLoading] = useState(false)
  const [preview, setPreview] = useState<PagePreviewState | null>(null)
  const [pageUnknown, setPageUnknown] = useState(false)
  const [availablePages, setAvailablePages] = useState<Array<number>>([])
  const [maxKnownPage, setMaxKnownPage] = useState<number | null>(null)
  const previewCacheRef = useRef(new Map<number, PagePreviewState>())
  const pagesLoadPromiseRef = useRef<Promise<void> | null>(null)
  const backgroundPrefetchRef = useRef<Promise<void> | null>(null)
  const pageCountRef = useRef<number | null>(null)
  const pageWaitersRef = useRef(
    new Map<number, Array<(page: PagePreviewState | null) => void>>(),
  )

  useEffect(() => {
    previewCacheRef.current.clear()
    pagesLoadPromiseRef.current = null
    backgroundPrefetchRef.current = null
    pageCountRef.current = null
    pageWaitersRef.current.clear()
    setPagesLoading(false)
    setAvailablePages([])
    setMaxKnownPage(null)
    setPreview(null)
  }, [documentToken])

  useEffect(() => {
    if (!documentToken) return
    let cancelled = false
    async function load() {
      setLoading(true)
      try {
        const loaded = await knowledgeApi.getDocument(documentToken!)
        if (!cancelled) {
          setDoc(loaded)
          setInstructionsDraft(loaded.instructions ?? '')
        }
      } catch (error: unknown) {
        const message =
          (error as { response?: { data?: { message?: string } } })?.response?.data
            ?.message ?? i18n.t('library.preview.toastNotFound', { ns: 'app' })
        toast.error(message)
        if (!cancelled) setDoc(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [documentToken])

  useKnowledgeDocumentIndexingUpdates((event) => {
    if (!documentToken || event.documentToken !== documentToken) return
    setDoc((prev) => {
      if (!prev) return prev
      return {
        ...prev,
        indexingStatus: event.indexingStatus as ContextMaterialIndexingStatus,
        indexingError: event.indexingError,
      }
    })
    if (isContextMaterialIndexingReady(event.indexingStatus)) {
      void knowledgeApi
        .getDocument(documentToken)
        .then((next) => setDoc(next))
        .catch(() => {
          /* keep patched status if refetch fails */
        })
    }
  })

  function rememberPages(
    pages: Array<{
      pageNumber: number
      text: string
      imageBase64?: string | null
      imageUrl?: string | null
    }>,
  ) {
    for (const page of pages) {
      const nextPreview: PagePreviewState = {
        pageNumber: page.pageNumber,
        text: page.text,
        imageBase64: page.imageBase64,
        imageUrl: page.imageUrl,
      }
      previewCacheRef.current.set(page.pageNumber, nextPreview)
      const waiters = pageWaitersRef.current.get(page.pageNumber)
      if (waiters?.length) {
        for (const resolve of waiters) {
          resolve(nextPreview)
        }
        pageWaitersRef.current.delete(page.pageNumber)
      }
    }
  }

  function waitForPage(pageNumber: number): Promise<PagePreviewState | null> {
    const cached = previewCacheRef.current.get(pageNumber)
    if (cached) {
      return Promise.resolve(cached)
    }
    return new Promise((resolve) => {
      const existing = pageWaitersRef.current.get(pageNumber) ?? []
      existing.push(resolve)
      pageWaitersRef.current.set(pageNumber, existing)
    })
  }

  async function prefetchPageRange(fromPage: number, toPage: number) {
    if (!documentToken) return 0
    const result = await knowledgeApi.getAllPagePreviews(documentToken, {
      fromPage,
      toPage,
    })
    rememberPages(result.pages)
    pageCountRef.current = result.pageCount
    setMaxKnownPage(result.pageCount)
    // Show full page rail once we know the count; content fills in by batch.
    setAvailablePages(
      Array.from({ length: result.pageCount }, (_, i) => i + 1),
    )
    return result.pageCount
  }

  function startBackgroundPrefetch(pageCount: number) {
    if (!documentToken || backgroundPrefetchRef.current) return
    backgroundPrefetchRef.current = (async () => {
      for (
        let fromPage = PAGE_BATCH_SIZE + 1;
        fromPage <= pageCount;
        fromPage += PAGE_BATCH_SIZE
      ) {
        const toPage = Math.min(fromPage + PAGE_BATCH_SIZE - 1, pageCount)
        try {
          await prefetchPageRange(fromPage, toPage)
        } catch {
          // Keep whatever we already have; later clicks can retry via selectPage.
          break
        }
      }
    })().finally(() => {
      backgroundPrefetchRef.current = null
    })
  }

  async function ensureInitialPagesLoaded(): Promise<boolean> {
    if (!documentToken) return false
    if (previewCacheRef.current.has(1) || previewCacheRef.current.has(2)) {
      return true
    }
    if (pagesLoadPromiseRef.current) {
      try {
        await pagesLoadPromiseRef.current
        return previewCacheRef.current.size > 0
      } catch {
        return false
      }
    }

    setPagesLoading(true)
    pagesLoadPromiseRef.current = (async () => {
      await prefetchPageRange(1, PAGE_BATCH_SIZE)
    })()
      .catch((error) => {
        pagesLoadPromiseRef.current = null
        throw error
      })
      .finally(() => {
        setPagesLoading(false)
      })

    try {
      await pagesLoadPromiseRef.current
      return previewCacheRef.current.size > 0
    } catch {
      return false
    }
  }

  async function ensurePageAvailable(
    pageNumber: number,
  ): Promise<PagePreviewState | null> {
    const cached = previewCacheRef.current.get(pageNumber)
    if (cached) return cached

    const loaded = await ensureInitialPagesLoaded()
    if (!loaded) return null

    const afterInitial = previewCacheRef.current.get(pageNumber)
    if (afterInitial) return afterInitial

    const total = pageCountRef.current
    if (total != null && pageNumber > total) {
      return null
    }

    // Request the batch of 2 that contains this page while background prefetch continues.
    const batchStart =
      Math.floor((pageNumber - 1) / PAGE_BATCH_SIZE) * PAGE_BATCH_SIZE + 1
    const batchEnd = batchStart + PAGE_BATCH_SIZE - 1
    try {
      await prefetchPageRange(
        batchStart,
        total != null ? Math.min(batchEnd, total) : batchEnd,
      )
    } catch {
      return previewCacheRef.current.get(pageNumber) ?? null
    }
    return (
      previewCacheRef.current.get(pageNumber) ??
      (await waitForPage(pageNumber))
    )
  }

  /**
   * Show one page in the preview pane. `syncUrl` is off for selections that
   * came *from* the URL, so the effect below can never loop; `notify` is off
   * for automatic selections so an empty document does not toast on load.
   */
  async function selectPage(
    pageNumber: number,
    options: {
      syncUrl?: boolean
      notify?: boolean
      /** The deep link asked for a page we could not resolve. */
      markUnknown?: boolean
    } = {},
  ) {
    const { syncUrl = true, notify = true, markUnknown = false } = options
    if (!documentToken) return

    const target = await ensurePageAvailable(pageNumber)
    if (!target) {
      if (notify) {
        toast.error(i18n.t('library.preview.toastNoPage', { ns: 'app', page: pageNumber }))
      }
      return
    }
    setPreview(target)
    setPageUnknown(markUnknown)
    if (pageCountRef.current) {
      startBackgroundPrefetch(pageCountRef.current)
    }
    if (syncUrl) {
      onPageResolved?.(target.pageNumber)
    }
  }

  const title = doc ? documentTitle(doc) : i18n.t('library.type.document', { ns: 'app' })

  // Page spans back every renderer, not just the paged ones: a website falls
  // back to the captured text when the frame is blocked, and audio/video use
  // it as the transcript. So every document loads its first span.
  useEffect(() => {
    if (!documentToken || loading || !doc) {
      return
    }
    if (initialPage != null) {
      if (!Number.isFinite(initialPage) || initialPage < 1) {
        void selectPage(1, {
          syncUrl: false,
          notify: false,
          markUnknown: true,
        })
        return
      }
      if (preview?.pageNumber === initialPage) {
        return
      }
      void selectPage(initialPage, { syncUrl: false, notify: false })
      return
    }
    if (initialPageUnknown) {
      void selectPage(1, { syncUrl: false, notify: false, markUnknown: true })
      return
    }
    if (preview) {
      return
    }
    void selectPage(1, { syncUrl: false, notify: false })
    // The loader itself is ref-guarded; re-running on preview changes is safe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentToken, loading, doc?.token, initialPage, initialPageUnknown, preview?.pageNumber])

  async function handleDownload() {
    if (!documentToken || downloading) return
    setDownloading(true)
    try {
      const { downloadUrl, filename } = await knowledgeApi.downloadDocument(documentToken)
      if (!downloadUrl) {
        toast.error(i18n.t('library.preview.toastDownloadUnavailable', { ns: 'app' }))
        return
      }
      // `window.document` on purpose: `doc` shadows nothing here, but the DOM
      // document must never be confused with the knowledge document.
      const link = window.document.createElement('a')
      link.href = downloadUrl
      link.download = filename || 'document'
      link.rel = 'noopener'
      window.document.body.appendChild(link)
      link.click()
      link.remove()
      window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1_000)
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? i18n.t('library.preview.toastDownloadFailed', { ns: 'app' })
      toast.error(message)
    } finally {
      setDownloading(false)
    }
  }

  async function handleDelete() {
    if (!documentToken || deleting) return
    setDeleting(true)
    try {
      await knowledgeApi.deleteDocument(documentToken)
      toast.success(i18n.t('library.preview.toastDeleted', { ns: 'app' }))
      onDeleted?.()
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? i18n.t('library.preview.toastDeleteFailed', { ns: 'app' })
      toast.error(message)
    } finally {
      setDeleting(false)
    }
  }

  async function handleSaveDetails(payload: { title?: string; summary?: string }) {
    if (!documentToken || savingDetails) return
    setSavingDetails(true)
    try {
      const updated = await knowledgeApi.updateDocument(documentToken, payload)
      setDoc(updated)
      toast.success(i18n.t('library.preview.toastUpdated', { ns: 'app' }))
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? i18n.t('library.preview.toastUpdateFailed', { ns: 'app' })
      toast.error(message)
    } finally {
      setSavingDetails(false)
    }
  }

  async function handleConfirmFacet(facet: KnowledgeDocumentFacet) {
    if (!documentToken || facetBusy) return
    setFacetBusy(true)
    try {
      const next = await knowledgeApi.confirmFacet(
        documentToken,
        facet.assignmentToken,
      )
      setDoc(next)
      toast.success(i18n.t('library.preview.toastFacetConfirmed', { ns: 'app' }))
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? i18n.t('library.preview.toastFacetConfirmFailed', { ns: 'app' })
      toast.error(message)
    } finally {
      setFacetBusy(false)
    }
  }

  async function handleCorrectFacet(
    facet: KnowledgeDocumentFacet,
    value: string,
  ) {
    if (!documentToken || facetBusy || !value.trim()) return
    setFacetBusy(true)
    try {
      const next = await knowledgeApi.correctFacet(
        documentToken,
        facet.assignmentToken,
        { kind: facet.kind, value: value.trim() },
      )
      setDoc(next)
      toast.success(i18n.t('library.preview.toastFacetUpdated', { ns: 'app' }))
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? i18n.t('library.preview.toastFacetUpdateFailed', { ns: 'app' })
      toast.error(message)
    } finally {
      setFacetBusy(false)
    }
  }

  async function handleRejectFacet(facet: KnowledgeDocumentFacet) {
    if (!documentToken || facetBusy) return
    setFacetBusy(true)
    try {
      const updated = await knowledgeApi.rejectFacet(
        documentToken,
        facet.assignmentToken,
      )
      setDoc(updated)
      toast.success(i18n.t('library.preview.toastFacetRejected', { ns: 'app' }))
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? i18n.t('library.preview.toastFacetRejectFailed', { ns: 'app' })
      toast.error(message)
    } finally {
      setFacetBusy(false)
    }
  }

  async function handleConfirmAllFacets() {
    if (!documentToken || facetBusy) return
    setFacetBusy(true)
    try {
      const updated = await knowledgeApi.confirmAllFacets(documentToken)
      setDoc(updated)
      toast.success(i18n.t('library.preview.toastFacetsConfirmed', { ns: 'app' }))
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? i18n.t('library.preview.toastFacetsConfirmFailed', { ns: 'app' })
      toast.error(message)
    } finally {
      setFacetBusy(false)
    }
  }

  async function handleSaveInstructions() {
    if (!documentToken || instructionsBusy) return
    setInstructionsBusy(true)
    try {
      const updated = await knowledgeApi.updateInstructions(
        documentToken,
        instructionsDraft,
      )
      setDoc(updated)
      setInstructionsDraft(updated.instructions ?? '')
      toast.success(i18n.t('library.preview.toastInstructionsSaved', { ns: 'app' }))
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? i18n.t('library.preview.toastInstructionsFailed', { ns: 'app' })
      toast.error(message)
    } finally {
      setInstructionsBusy(false)
    }
  }

  async function handleReplaceFile(file: File) {
    if (!documentToken || replacing) return
    setReplacing(true)
    try {
      const updated = await knowledgeApi.replaceDocument(documentToken, file)
      setDoc(updated)
      setInstructionsDraft(updated.instructions ?? '')
      previewCacheRef.current.clear()
      pagesLoadPromiseRef.current = null
      backgroundPrefetchRef.current = null
      pageCountRef.current = null
      pageWaitersRef.current.clear()
      setAvailablePages([])
      setMaxKnownPage(null)
      setPreview(null)
      toast.success(i18n.t('library.preview.toastFileReplaced', { ns: 'app' }), {
        description: i18n.t('library.preview.toastReindexing', { ns: 'app' }),
      })
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? i18n.t('library.preview.toastReplaceFailed', { ns: 'app' })
      toast.error(message)
    } finally {
      setReplacing(false)
      if (replaceInputRef.current) {
        replaceInputRef.current.value = ''
      }
    }
  }

  const versions = doc
    ? doc.versions?.length
      ? doc.versions
      : [
          {
            label: 'Current',
            version: 'v1',
            createdAt: doc.createdAt,
            isCurrent: true,
            ownerName: doc.ownerName,
          },
        ]
    : []

  return {
    doc,
    setDoc,
    loading,
    title,
    versions,
    downloading,
    deleting,
    facetBusy,
    savingDetails,
    instructionsDraft,
    setInstructionsDraft,
    instructionsBusy,
    replacing,
    replaceInputRef,
    pagesLoading,
    preview,
    pageUnknown,
    availablePages,
    maxKnownPage,
    startAt,
    selectPage,
    handleDownload,
    handleDelete,
    handleSaveDetails,
    handleConfirmFacet,
    handleCorrectFacet,
    handleRejectFacet,
    handleConfirmAllFacets,
    handleSaveInstructions,
    handleReplaceFile,
  }
}
